import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Features, QuestionType } from '../../../../generated/hooks';
import type { NewReport } from '../../hooks/useNewReport';
import type { ReportQuestion } from '../../types';
import {
  newDefinitionId,
  type ReportComponent,
  type ReportDefinitionDocument,
  type ReportMetricDefinition,
} from '../types';
import { makeMetric } from './MetricEditor';
import { useDefinition } from '../ReportDefinitionContext';

export type TableComponent = Extract<ReportComponent, { type: 'table' }>;
export type ComponentWidth = NonNullable<ReportComponent['width']>;
/** Which editor dialog is open. The snapshot lets a metric dialog restore the document on cancel. */
export type EditorModalState =
  | { kind: 'question'; index: number }
  | {
      kind: 'metric';
      metricId: string;
      placementId?: string;
      index?: number;
      create?: boolean;
      snapshot: ReportDefinitionDocument;
    }
  | { kind: 'library'; index?: number }
  | { kind: 'table'; component?: TableComponent; index?: number }
  | { kind: 'save' }
  | null;

const REPORTABLE_QUESTION_TYPES = [
  QuestionType.Select,
  QuestionType.Scale,
  QuestionType.Multiselect,
  QuestionType.Freetext,
];
export const hasWidth = (component: ReportComponent) =>
  component.type === 'metric' || component.type === 'responseRate' || component.type === 'anonymityRate';
export const componentSpan = (component: ReportComponent): number =>
  hasWidth(component) ? ({ full: 12, half: 6, third: 4, quarter: 3 } as const)[component.width ?? 'full'] : 12;

/** Group consecutive components into visual rows the same way sparse CSS grid auto-placement does for 12/6/4/3 spans. */
export function computeRows(components: ReportComponent[]): { start: number; components: ReportComponent[] }[] {
  const rows: { start: number; components: ReportComponent[] }[] = [];
  let used = 0;
  components.forEach((component, index) => {
    const span = componentSpan(component);
    if (!rows.length || used + span > 12) {
      rows.push({ start: index, components: [component] });
      used = span;
    } else {
      rows[rows.length - 1].components.push(component);
      used += span;
    }
  });
  return rows;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyActions = Record<string, (...args: any[]) => any>;

/**
 * Returns functions whose identity never changes but which always call the latest implementation, so memoized
 * blocks and insert zones are not re-rendered just because the editor closed over a newer document.
 */
function useStableActions<T extends AnyActions>(actions: T): T {
  const latest = useRef(actions);
  useLayoutEffect(() => {
    latest.current = actions;
  }, [actions]);
  const [stable] = useState(
    () =>
      Object.fromEntries(
        Object.keys(actions).map((key) => [key, (...args: unknown[]) => latest.current[key](...args)]),
      ) as T,
  );
  return stable;
}

/** The editor's state and actions. Created once by `OverviewEditorProvider`; read through `useEditor()`. */
export function useOverviewEditor(report: NewReport) {
  const { t, i18n } = useTranslation('newReport');
  const controller = useDefinition();
  const document = controller.document;
  const components = document.pages.overview.components;
  const [modal, setModal] = useState<EditorModalState>(null);
  const [focusId, setFocusId] = useState<string>();
  const language = i18n.language;
  const editing = controller.open && !controller.previewId;

  const updateComponents = (next: ReportComponent[]) =>
    controller.edit((previous) => ({
      ...previous,
      pages: { ...previous.pages, overview: { ...previous.pages.overview, components: next } },
    }));
  const insertAt = (component: ReportComponent, index: number) => {
    const at = Math.max(0, Math.min(components.length, index));
    updateComponents([...components.slice(0, at), component, ...components.slice(at)]);
  };
  const move = (from: number, to: number) => {
    if (from < 0 || to < 0 || from >= components.length || to >= components.length || from === to) return;
    const next = [...components];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    updateComponents(next);
  };
  /** Insert the dragged component before the component currently at `insertion` (or at the end). */
  const dropAt = (id: string, insertion: number) => {
    const from = components.findIndex((item) => item.id === id);
    if (from < 0) return;
    move(from, Math.min(components.length - 1, insertion - (from < insertion ? 1 : 0)));
  };
  const remove = (id: string) => updateComponents(components.filter((item) => item.id !== id));
  const updateComponent = (component: ReportComponent) =>
    updateComponents(components.map((item) => (item.id === component.id ? component : item)));
  const upsertMetric = (metric: ReportMetricDefinition) =>
    controller.edit((previous) => ({
      ...previous,
      metrics: previous.metrics.some((item) => item.id === metric.id)
        ? previous.metrics.map((item) => (item.id === metric.id ? metric : item))
        : [...previous.metrics, metric],
    }));
  const removeMetric = (id: string) =>
    controller.edit((previous) => ({ ...previous, metrics: previous.metrics.filter((item) => item.id !== id) }));
  const placeMetric = (metricId: string, index?: number) =>
    insertAt({ id: newDefinitionId(), type: 'metric', metricId, width: 'half' }, index ?? components.length);
  const addSection = (index: number) => {
    const section: ReportComponent = { id: newDefinitionId(), type: 'section', label: { [language]: '' } };
    insertAt(section, index);
    setFocusId(section.id);
  };
  const openMetric = (metricId: string, options: { placementId?: string; index?: number } = {}) =>
    setModal({ kind: 'metric', metricId, ...options, snapshot: document });
  const createMetric = (index?: number, questionIndex?: string) => {
    const question = report.rawData?.questions.find((item) => item.index === questionIndex);
    const metric = makeMetric(question, language, question?.text ?? t('definition.newMetric'));
    upsertMetric(metric);
    setModal({ kind: 'metric', metricId: metric.id, index, create: true, snapshot: document });
  };

  const reportableQuestions = useMemo(
    (): ReportQuestion[] =>
      (report.rawData?.questions ?? []).filter((question) => REPORTABLE_QUESTION_TYPES.includes(question.type)),
    [report.rawData],
  );
  const placedQuestionIndexes = useMemo(
    () => new Set(components.flatMap((component) => (component.type === 'question' ? [component.questionIndex] : []))),
    [components],
  );
  const unplacedQuestions = useMemo(
    () => reportableQuestions.filter((question) => !placedQuestionIndexes.has(question.index)),
    [reportableQuestions, placedQuestionIndexes],
  );
  /** Built-in rate components that can be put back; anonymity only makes sense once someone answered anonymously. */
  const hasAnonymousResponses = useMemo(
    () =>
      report.rawData?.responses.some(
        (response) => response.anonymous || response.originalAnonymous || response.additionallyAnonymized,
      ) ?? false,
    [report.rawData],
  );
  const unplacedRates = (['responseRate', 'anonymityRate'] as const).filter(
    (type) =>
      (type === 'responseRate' || hasAnonymousResponses) && !components.some((component) => component.type === type),
  );
  const placeRate = (type: 'responseRate' | 'anonymityRate', index?: number) =>
    insertAt({ id: newDefinitionId(), type, width: 'full' }, index ?? components.length);
  const canPlaceAiSummary =
    !!report.rawData?.metadata.organization.featureNames?.includes(Features.AiSummaryOfSurveyAndPhase) &&
    !components.some((component) => component.type === 'aiSummary');
  const placeAiSummary = (index: number) => {
    if (canPlaceAiSummary) insertAt({ id: newDefinitionId(), type: 'aiSummary' }, index);
  };

  const actions = useStableActions({
    setModal,
    clearFocus: () => setFocusId(undefined),
    updateComponents,
    insertAt,
    move,
    dropAt,
    remove,
    updateComponent,
    upsertMetric,
    removeMetric,
    placeMetric,
    addSection,
    openMetric,
    createMetric,
    placeRate,
    placeAiSummary,
  });

  return {
    ...actions,
    actions,
    controller,
    document,
    components,
    editing,
    language,
    modal,
    focusId,
    reportableQuestions,
    unplacedQuestions,
    unplacedRates,
    canPlaceAiSummary,
  };
}

export type OverviewEditor = ReturnType<typeof useOverviewEditor>;
/** Identity-stable editor actions, safe as props of memoized components. */
export type OverviewEditorActions = OverviewEditor['actions'];
